import React from "react";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseInput from "@reusables/BaseInputLegacy";
import { useTranslation } from "react-i18next";
import { Transfer } from "@/types/general";
import { useAppSelector } from "@/redux/hooks";
import { FileText } from "lucide-react";

type ViewLayoutProperties = {
    transfer: Transfer.Root;
};

export default function ViewLayout({ transfer }: ViewLayoutProperties) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockTransfers.stockTransfer" });

    return (
        <>
            <div className="space-y-[40px]">
                <BaseInputsGrid>
                    <InputLikeContainer
                        label={t("fields.product.label")}
                        content={transfer.product.name}
                    />

                    <InputLikeContainer
                        label={t("fields.fromLocation.label")}
                        content={transfer.location_from.store}
                    />

                    <InputLikeContainer
                        label={t("fields.section.label")}
                        content={transfer.section_from?.name ?? "-"}
                    />

                    <InputLikeContainer
                        label={t("fields.toLocation.label")}
                        content={transfer.location_to.name}
                    />

                    <InputLikeContainer
                        label={t("fields.section.label")}
                        content={transfer.section_to?.name ?? "-"}
                    />

                    <InputLikeContainer
                        label={t("fields.date.label")}
                        content={transfer.date.format("YYYY-MM-DD")}
                    />

                    <BaseInput
                        label={t("fields.availableQuantity.label")}
                        value={transfer.old_quantity.toString()}
                        disabled
                    />

                    <InputLikeContainer
                        label={t("fields.transferQuantity.label")}
                        content={transfer.quantity.toString()}
                    />

                    {transfer.has_attachment && (
                        <InputLikeContainer
                            label={t("fields.files.label")}
                            content={transfer.attachment.file_name}
                            downloadUrl={transfer.attachment.download_url}
                        />
                    )}
                </BaseInputsGrid>

                <InputLikeContainer
                    label={t("fields.remarks.label")}
                    content={transfer.remarks ?? "-"}
                />
            </div>
        </>
    );
}

function InputLikeContainer({
    label,
    content,
    downloadUrl,
}: {
    label: string;
    content: JSX.Element | string | undefined;
    downloadUrl?: string;
}) {
    const token = useAppSelector((state) => state.auth.tokens?.access);

    const handleDownload = async () => {
        if (!downloadUrl) return;

        const response = await fetch(downloadUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error("Download failed");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = typeof content === "string" ? content : "file";
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const clickable = Boolean(downloadUrl);

    return (
        <div>
            <div className="font-semibold text-inputs-label-dim mb-[24px]">{label}</div>

            <div
                className={`flex items-center gap-3 text-accent ${
                    clickable ? "cursor-pointer hover:underline" : ""
                }`}
                onClick={clickable ? handleDownload : undefined}
            >
                {clickable && <FileText size={20} />}
                <div>{content}</div>
            </div>
        </div>
    );
}
